import React, { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme, Avatar, Button, Input, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useBand, useBandShows, useBandMusicians, useBandGenres, useBandShowComments, useBandShowMedia, useBandShowContributors } from '../../api/queries';
import { claim_band, delete_band } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import SpecModal from '../../components/SpecModal/SpecModal';
import {
  ProfileLayout,
  AboutSection,
  GenreTagsSection,
  UpcomingShowsSection,
  PeopleListSection,
  ShowPhotosSection,
  ShowCommentsSection,
} from '../../components/Profile';
import ProfileSection from '../../components/Profile/sections/ProfileSection';
import type { ActionSheetItem } from '../../components/SpecActionSheet/SpecActionSheet';
import type { ShowListingProps } from '../../components/DirectoryListings/ShowListing/ShowListing';
import type { PersonItem } from '../../components/Profile';
import type { CommentProps } from '../../components/Comment/Comment';
import type { PhotoMetadata } from '../../components/PhotoGallery/PhotoOverlayInfo';
import { BandGroupIcon, ChatIcon, UserGroupIcon, PlusCircleIcon } from '../../components/icons';
import { UserTag } from '../../components/UserTag/UserTag';
import type { UserType } from '../../components/UserTag/UserTag';
import UserTypeInfo from '../../components/UserTypeInfo/UserTypeInfo';
import BandMembersDrawer from '../../components/ManageDrawer/BandMembersDrawer';
import ShowContributorsDrawer from '../../components/ManageDrawer/ShowContributorsDrawer';
import SpecAlert from '../../components/SpecAlert/SpecAlert';
import { useShare } from '../../hooks/useShare';
import { getShowDateTime } from '../../utils/showDateTime';
import '../EditProfile.css';

dayjs.extend(isSameOrAfter);

const DELETE_PASSCODE = 'deletemyband';

const BandDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const queryClient = useQueryClient();
  const { user, refreshAuth } = useAuth();
  const { data: band, isLoading, error } = useBand(id);
  const [claimLoading, setClaimLoading] = useState(false);
  const { data: bandShows } = useBandShows(id);
  const { data: bandMusicians } = useBandMusicians(id);
  const { data: bandGenres } = useBandGenres(id);
  const { data: bandShowComments } = useBandShowComments(id);
  const { data: bandShowMedia } = useBandShowMedia(id);
  const { data: bandShowContributors } = useBandShowContributors(id);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const [bandMembersDrawerOpen, setBandMembersDrawerOpen] = useState(false);
  const [showContributorsDrawerOpen, setShowContributorsDrawerOpen] = useState(false);
  const toastKeyRef = useRef(0);
  const showToast = useCallback((msg: string) => { toastKeyRef.current += 1; setToastMessage(msg); }, []);
  const share = useShare(showToast);

  const handleClaimBand = async () => {
    if (!id || !user) return;

    setClaimLoading(true);
    try {
      await claim_band(id);
      message.success('Band claimed successfully! You are now the band admin.');
      // Refresh the band data and band members
      queryClient.invalidateQueries({ queryKey: ['band', id] });
      queryClient.invalidateQueries({ queryKey: ['bandMusicians', id] });
      // Refresh auth to get updated claims with band-admin role
      await refreshAuth();
    } catch (err: any) {
      message.error(err.message || 'Failed to claim band');
    } finally {
      setClaimLoading(false);
    }
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [deleting, setDeleting] = useState(false);

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletePasscode('');
  };

  const handleDeleteBand = async () => {
    if (!band) return;
    setDeleting(true);
    try {
      await delete_band(band.id);
      navigate('/home');
    } catch (err: any) {
      showToast(err.message || 'Failed to delete band');
      setDeleting(false);
    }
  };

  // Mirrors isAdminPermission() on the backend exactly (permissions.includes('*:*')),
  // not the looser roles.includes('admin') idiom used elsewhere in the frontend -
  // this is the literal condition that unlocks the bypass server-side.
  const isAdmin = !!user?.claims?.permissions?.includes('*:*');
  // Owner (claimed_by_user_id) or any current band-admin (bandRoles) may edit -
  // unlike musician profile, a band can have admins added after the fact who
  // never claimed it themselves. Mirrors bandController.update's ownership check.
  const isOwner = !!band && !!user && band.claimed_by_user_id === user.uid;
  const isBandAdmin = !!band && !!user && user.claims?.bandRoles?.[band.id]?.role === 'band-admin';
  const canEdit = isOwner || isBandAdmin || isAdmin;
  // Delete stays owner-only (+ admin bypass), matching bandController.delete's
  // stricter check - a band-admin who isn't the claimer would get a 403 on this one.
  const canDelete = isOwner || isAdmin;

  const actionItems: ActionSheetItem[] = band ? [
    { key: 'share', label: 'Share', onClick: () => share({ title: band.name, entityType: 'band', entityId: id }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); showToast('Link Copied'); } },
    { key: 'qr-code', label: 'QR Code', onClick: () => navigate(`/band/${id}/qr`) },
    ...(canEdit ? [{ key: 'edit-band', label: 'Edit Band', onClick: () => navigate(`/edit-band-profile/${id}`) }] : []),
    ...(canDelete ? [{ key: 'delete-band', label: 'Delete Band', danger: true, onClick: () => setDeleteModalOpen(true) }] : []),
  ] : [];

  if (isLoading) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        Loading band...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorError }}>
        Error loading band: {error.message}
      </div>
    );
  }

  if (!band) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        Band not found.
      </div>
    );
  }

  // Handle both 'bio' (from API) and 'description' (from interface) fields
  const aboutText = band.bio || band.description;

  // Use genres from dedicated endpoint (extract genre_name), fallback to band data
  const genres: string[] = bandGenres && bandGenres.length > 0
    ? bandGenres.map(g => g.genre_name)
    : band.genres && band.genres.length > 0
      ? band.genres
      : band.genre
        ? [band.genre]
        : [];

  // Location info from API
  const location = band.city && band.state
    ? `${band.city}, ${band.state}`
    : band.city || band.state;

  // Format date to "Day Month Date" format (e.g., "Wed Sep 17")
  const formatDate = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format timestamp for comments (e.g., "Jan 15 3:29 PM")
  const formatTimestamp = (timestamp: string | { seconds: number } | undefined) => {
    if (!timestamp) return '';

    // Handle Firestore Timestamp object
    if (typeof timestamp === 'object' && 'seconds' in timestamp) {
      return dayjs.unix(timestamp.seconds).format('MMM D h:mm A');
    }

    // Handle ISO string or other date string
    const parsed = dayjs(timestamp);
    if (!parsed.isValid()) return '';
    return parsed.format('MMM D h:mm A');
  };

  // Extract display name from what might be an email (use part before @)
  const formatUserName = (name: string | undefined) => {
    if (!name) return 'Anonymous';
    if (name.includes('@')) return name.split('@')[0];
    return name;
  };

  // Filter band shows into upcoming (today and future) and sort by date
  const today = dayjs().startOf('day');

  const allShowsTransformed = (bandShows || [])
    .map(show => ({
      id: show.id,
      bandName: show.band_name || band.name,
      date: formatDate(show.date),
      rawDate: show.date,
      venue: show.venue_name || show.address || 'TBA',
      time: show.time,
      description: show.description,
      imageUrl: show.band_photo_url || band.photo_url,
    }))
    .sort((a, b) => getShowDateTime(a.rawDate, a.time) - getShowDateTime(b.rawDate, b.time));

  const upcomingShows = allShowsTransformed
    .filter(show => dayjs(show.rawDate).startOf('day').isSameOrAfter(today))
    .map(({ rawDate, ...show }) => show);

  // Only show upcoming shows in the main section
  const showsToDisplay = upcomingShows;

  // Transform band musicians to PersonItem format
  const bandMembers: PersonItem[] = (bandMusicians || []).map(musician => ({
    name: `${musician.first_name} ${musician.last_name}`,
    avatarUrl: musician.photo_url,
    route: `/musicians/${musician.id}`,
  }));

  // Transform show contributors to PersonItem format (deduplicated by user_id)
  const showContributors: PersonItem[] = (() => {
    const seen = new Set<string>();
    return (bandShowContributors || [])
      .filter(contributor => {
        if (!contributor.user_id || seen.has(contributor.user_id)) {
          return false;
        }
        seen.add(contributor.user_id);
        return true;
      })
      .map(contributor => ({
        name: contributor.user_name || contributor.user_email || 'Unknown User',
      }));
  })();

  // Transform show comments to CommentProps format
  const comments: Omit<CommentProps, 'className'>[] = (bandShowComments || []).map(comment => {
    const rawUserId = comment.user_id || comment.created_by_id;
    const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
    return {
      userName: formatUserName(comment.user_name),
      avatarUrl: comment.user_avatar_url,
      timestamp: formatTimestamp(comment.timestamp || comment.created_at),
      userTags: (comment.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      text: comment.comment,
      userProfileUrl: userId ? `/fans/${userId}` : undefined,
      onUserClick: userId ? () => navigate(`/fans/${userId}`) : undefined,
      showEventInfo: !!(comment.band_name && comment.show_date && comment.venue_name),
      eventInfo: (comment.band_name && comment.show_date && comment.venue_name) ? {
        artistName: comment.band_name,
        artistAvatarUrl: comment.band_photo_url,
        date: comment.show_date,
        venue: comment.venue_name,
      } : undefined,
    };
  });

  // Transform show media to photos array and metadata
  const photos: string[] = (bandShowMedia || []).map(media => media.url);
  const photoMetadata: PhotoMetadata[] = (bandShowMedia || []).map(media => {
    const rawUserId = media.user_id || media.created_by_id;
    const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
    return {
      userName: formatUserName(media.user_name),
      userAvatarUrl: media.user_avatar_url,
      timestamp: formatTimestamp(media.timestamp || media.created_at),
      userTags: (media.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      userProfileUrl: userId ? `/fans/${userId}` : undefined,
      eventInfo: (media.band_name && media.show_date && media.venue_name) ? {
        artistName: media.band_name,
        artistAvatarUrl: media.band_photo_url,
        date: media.show_date,
        venue: media.venue_name,
      } : undefined,
    };
  });

  return (
    <ProfileLayout
      profileType="band"
      heroVariant="blurred"
      heroImageUrl={band.photo_url}
      showPlayButton={!!band.soundcloud_url}
      soundcloudUrl={band.soundcloud_url}
      actionSheetItems={actionItems}
    >
      <div className="band-identity">
        <div className="band-identity__avatar">
          {band.photo_url ? (
            <img className="band-identity__avatar-img" src={band.photo_url} alt={band.name} />
          ) : (
            <div className="band-identity__avatar-placeholder">
              <BandGroupIcon size={48} />
            </div>
          )}
        </div>
        <div className="band-identity__info">
          <h1 className="band-identity__name">{band.name}</h1>
          {location && (
            <p className="band-identity__subtitle" style={{ margin: '4px 0 8px', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
              {location}
            </p>
          )}
          <div className="band-identity__badges">
            <UserTag type="band" onClick={() => setOpenInfoType('band')} />
          </div>
        </div>
        <UserTypeInfo type="band" open={openInfoType === 'band'} onClose={() => setOpenInfoType(null)} />
        {bandMembers.length > 0 && (
          <div
            className="band-identity__members"
            onClick={() => document.getElementById('band-members-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <Avatar.Group size="default" max={{ count: 3, style: { backgroundColor: '#252B2D', color: '#fff', fontSize: 12 } }}>
              {bandMembers.map((member, index) => (
                <Avatar
                  key={index}
                  src={member.avatarUrl}
                  style={!member.avatarUrl ? { backgroundColor: '#252B2D' } : undefined}
                />
              ))}
            </Avatar.Group>
            <span className="band-identity__members-text">
              With {bandMembers[0]?.name}{bandMembers.length > 1 ? ' and more...' : ''}
            </span>
          </div>
        )}
      </div>

      {aboutText && <AboutSection text={aboutText} />}

      {/* Shows */}
      <UpcomingShowsSection
        shows={showsToDisplay}
        allShows={allShowsTransformed}
        showAddButton
        addShowIcon={<ChatIcon size="s" tone="black" />}
        onAddShow={() => navigate('/chat?prompt=add-show')}
      />

      {/* Show Photos */}
      {photos.length > 0 ? (
        <ShowPhotosSection
          photos={photos}
          photoMetadata={photoMetadata}
          subtitle="Photos from the band's past shows"
        />
      ) : (
        <ProfileSection title="Show Photos" subtitle="Photos from the band's past shows">
          <div className="view-all-drawer__empty">No photos yet</div>
        </ProfileSection>
      )}

      {/* Show Comments */}
      {comments.length > 0 ? (
        <ShowCommentsSection
          comments={comments}
          showEventInfo
        />
      ) : (
        <ProfileSection title="Show Comments">
          <div className="view-all-drawer__empty">No comments yet</div>
        </ProfileSection>
      )}

      {/* Band Members */}
      <PeopleListSection
        id="band-members-section"
        title="Band Members"
        people={bandMembers}
        actionLabel="Band Members"
        actionIcon={<UserGroupIcon size="s" tone="black" />}
        onAction={() => setBandMembersDrawerOpen(true)}
      />

      {/* Show Contributors */}
      <PeopleListSection
        title="Show Contributors"
        people={showContributors}
        actionLabel="Show Contributors"
        actionIcon={<PlusCircleIcon width={16} height={16} color="black" />}
        onAction={() => setShowContributorsDrawerOpen(true)}
      />

      {/* Genres */}
      {genres.length > 0 ? (
        <GenreTagsSection genres={genres} />
      ) : (
        <ProfileSection title="Genres">
          <div className="view-all-drawer__empty">No genres specified</div>
        </ProfileSection>
      )}

      {/* Management Drawers */}
      <BandMembersDrawer
        open={bandMembersDrawerOpen}
        onClose={() => setBandMembersDrawerOpen(false)}
        bandName={band.name}
        bandId={id}
      />
      <ShowContributorsDrawer
        open={showContributorsDrawerOpen}
        onClose={() => setShowContributorsDrawerOpen(false)}
        bandName={band.name}
        bandId={id}
      />

      {/* Claim Band Section - Only shown to musicians */}
      {band.claimed !== true && user && user.claims?.roles?.includes('musician') && (
        <div style={{
          padding: '24px 16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          textAlign: 'center',
        }}>
          <p style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: 14,
            marginBottom: 16,
          }}>
            Are you a member of this band? Claim it to manage shows, members, and more.
          </p>
          <Button
            type="primary"
            size="large"
            loading={claimLoading}
            onClick={handleClaimBand}
            style={{ minWidth: 200 }}
          >
            Claim This Band
          </Button>
        </div>
      )}

      <SpecAlert key={toastKeyRef.current} message={toastMessage} onDone={() => setToastMessage(null)} />

      {/* Delete Band Modal */}
      <SpecModal
        open={deleteModalOpen}
        onCancel={closeDeleteModal}
        title="Delete Band"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Type {DELETE_PASSCODE} to permanently delete this band.
          This deletes the band profile only - members keep their own accounts and musician profiles.
        </p>
        <div className="edit-profile-delete-form">
          <span className="edit-profile-delete-label">Delete Passcode:</span>
          <Input
            placeholder={`Enter ${DELETE_PASSCODE} to delete`}
            value={deletePasscode}
            onChange={(e) => setDeletePasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={deletePasscode !== DELETE_PASSCODE || deleting}
            loading={deleting}
            onClick={handleDeleteBand}
          >
            DELETE BAND
          </Button>
        </div>
      </SpecModal>
    </ProfileLayout>
  );
};

export default BandDetail;
